import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {
  ActionIcon,
  Box,
  Card,
  Chip,
  createStyles,
  Divider,
  Grid,
  Group,
  Image,
  Radio,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import React, {useMemo} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {formatHasBothDomainTypes} from 'antibodies/AntibodyUtils';
import GermlineSelector from 'antibodies/germline/GermlineSelector';
import {UseAntibodyFormatsQueryResult} from 'antibodies/hooks/useAntibodyFormatsQuery';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import {buildAntibodySchemaFormatMaps} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NdcGermlineConfig,
  NdcSpreadsheetComponentFormatType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import AntibodyFormatPicker, {
  type AntibodyTileProps,
} from 'antibodies/schema-configuration/AntibodyFormatPicker';
import {assert} from 'Asserts';
import {OBJECTS_REGISTERABLE_PERMISSION} from 'auth/PermissionConstants';
import {type TagSchema} from 'constants/RegistryConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import SelectableCard from 'ui-library/inputs/SelectableCard';

const MULTIPLE_GERMLINES_SENTINEL = 'MULTIPLE_GERMLINES';

const useStyles = createStyles((theme) => ({
  formatPicker: {
    h4: {
      marginBottom: theme.spacing.sm,
    },
    p: {
      marginBottom: theme.spacing.md,
    },
  },
  startingTypeSection: {
    h4: {
      marginBottom: 0,
    },
  },
  variablePairsSection: {
    marginTop: theme.spacing.lg,
  },
  tooltipIcon: {
    cursor: 'help',
    color: theme.colors.gray[6],
  },
  banner: {
    marginTop: '16px',
  },
}));

function VariablePairsTooltipLabel(): JSX.Element {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder={true} w="350px">
      <Card.Section p="xs">
        <Group spacing="md" align="flex-start" noWrap={true}>
          <Box w="90px" h="90px">
            <Image
              src="/static/img/antibody-format-variable-pairs.svg"
              alt="Example of a variable pair on an IgG Monospecific Antibody"
            />
          </Box>
          {/* We need to re-enable wrapping since Group noWrap gets inherited */}
          <Stack spacing="lg" style={{flex: 1, minWidth: 0, whiteSpace: 'normal'}}>
            <Text size="sm" fw="normal">
              A variable pair is a custom entity that points to both the VH and VL domain sequences.
            </Text>
            <Text size="sm" fw="normal">
              By providing a variable pair, you don't have to input the VH and VL domains separately.
            </Text>
          </Stack>
        </Group>
      </Card.Section>
    </Card>
  );
}

export interface SelectFormatPaneProps {
  isInitializing: boolean;
  selectedFormat: AntibodyFormat | null;
  setSelectedFormat: (format: AntibodyFormat | null) => void;
  selectedSchemaId: string | null;
  setSelectedSchema: (
    schemaId: string | null,
    schemaName: string | null,
    registryId: string | null,
    format: AntibodyFormat | null
  ) => void;
  startingType: NdcSpreadsheetComponentFormatType;
  setStartingType: (startingType: NdcSpreadsheetComponentFormatType) => void;
  antibodyFormatsQuery: UseAntibodyFormatsQueryResult;
  germlineConfig: NdcGermlineConfig;
  setGermlineConfig: (germlineConfig: NdcGermlineConfig) => void;
  showGermlineSelectors: boolean;
  onGermlineSelected?: (metadata: {
    germline_id: string;
    germline_selector_type: string;
    use_multiple_germlines: boolean;
  }) => void;
}

/**
 * Nested data creation spreadsheet import modal step that allows the user to select the format of the
 * antibodies they want to create and the schema to use, and what data type they're starting from.
 */
export default function SelectFormatPane({
  isInitializing,
  selectedFormat,
  setSelectedFormat,
  selectedSchemaId,
  setSelectedSchema,
  startingType,
  setStartingType,
  antibodyFormatsQuery,
  germlineConfig,
  setGermlineConfig,
  showGermlineSelectors,
  onGermlineSelected,
}: SelectFormatPaneProps): JSX.Element {
  const {classes} = useStyles();
  const {status: antibodyFormatsQueryStatus, antibodyFormats} = antibodyFormatsQuery;
  const {status: implementedAntibodySchemaInterfacesQueryStatus, implementedAntibodySchemaInterfaces} =
    useImplementedAntibodySchemaInterfacesQuery();

  const {schemaIdToFormat, formatIdToSchemaIds, allSchemaIds} = useMemo(
    () =>
      buildAntibodySchemaFormatMaps(
        implementedAntibodySchemaInterfaces.filter(
          (implementedInterface) => !implementedInterface.schema.archived
        )
      ),
    [implementedAntibodySchemaInterfaces]
  );

  const selectedFormatHasVariablePairs = (selectedFormat?.distinctVariablePairs.length ?? 0) > 0;

  const selectedFormatHasBothDomainTypes = selectedFormat != null && formatHasBothDomainTypes(selectedFormat);

  const antibodyTileProps: Array<AntibodyTileProps> =
    antibodyFormatsQueryStatus === QueryResultType.SUCCESS
      ? antibodyFormats
          // Only show formats that have a defined antibody schema using this format
          .filter(({id}) => formatIdToSchemaIds.has(id))
          .map(
            (format): AntibodyTileProps => ({
              format,
              showLabels: false,
              disabled: false,
            })
          )
      : [];

  function onSelectFormatId(formatId: string): void {
    assert(antibodyFormatsQueryStatus === QueryResultType.SUCCESS, 'Expected antibody formats to be loaded');
    const format = antibodyFormats.find(({id}) => id === formatId);
    assert(format != null, 'Expected format to be found in antibodyFormats');
    setSelectedFormat(format);
  }

  const onSelectSchema = (tagSchema: TagSchema | null): void => {
    assert(
      tagSchema == null || schemaIdToFormat.has(tagSchema.id),
      'Expected schema to be found in schemaIdToFormat'
    );
    const format = tagSchema != null ? schemaIdToFormat.get(tagSchema.id)! : null;
    const registryId = tagSchema?.registryId ?? null;
    setSelectedSchema(tagSchema?.id ?? null, tagSchema?.name ?? null, registryId, format);
  };

  const filteredSchemaIds =
    selectedFormat == null ? allSchemaIds : formatIdToSchemaIds.get(selectedFormat.id)!;

  return (
    <div {...getTestProps({component: 'SelectFormatPane'})}>
      {antibodyFormatsQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein formats" />
        </div>
      )}

      {implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR && (
        <div className={classes.banner}>
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading protein schemas" />
        </div>
      )}

      <div className={classes.formatPicker}>
        <h4>Format</h4>
        <p>Select the format of the proteins you want to create</p>
        <AntibodyFormatPicker
          antibodyTileProps={antibodyTileProps}
          disabled={isInitializing}
          selectedFormat={selectedFormat?.id ?? null}
          setSelectedFormat={onSelectFormatId}
          enableCreateNew={false}
          onCreationPaneOpenChange={noop}
        />
      </div>

      <Grid mt="md">
        <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'schemaSelector'})}>
          <FormGroup hasError={implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.ERROR}>
            <FieldLabel label="Schema" isRequired={true} />
            {isInitializing || implementedAntibodySchemaInterfacesQueryStatus === QueryResultType.PENDING ? (
              <div>
                <HorizontalThrobber />
              </div>
            ) : (
              <TagSchemaSelector
                selectedTagSchemaId={selectedSchemaId}
                onChange={onSelectSchema}
                tagSchemaFilterFn={(tagSchema) => filteredSchemaIds.has(tagSchema.id)}
                shouldDefaultIfSingleItem={true}
                requiredPermissions={[OBJECTS_REGISTERABLE_PERMISSION]}
              />
            )}
          </FormGroup>
        </Grid.Col>
        {showGermlineSelectors && (
          <Grid.Col span={6} {...getTestProps({component: 'SelectFormatPane', element: 'germlineSelector'})}>
            <FormGroup>
              <FieldLabel label="Germline" isRequired={true} />
              <GermlineSelector
                selectedGermlineId={
                  germlineConfig.useMultipleGermlines
                    ? MULTIPLE_GERMLINES_SENTINEL
                    : germlineConfig.selectedGermlineId
                }
                displayName={germlineConfig.useMultipleGermlines ? 'Multiple germlines' : undefined}
                onSelectGermline={(germlineId: string) => {
                  const isMultiple = germlineId === MULTIPLE_GERMLINES_SENTINEL;
                  if (isMultiple) {
                    setGermlineConfig({
                      ...germlineConfig,
                      selectedGermlineId: null,
                      useMultipleGermlines: true,
                    });
                  } else {
                    setGermlineConfig({
                      selectedGermlineId: germlineId,
                      selectedVariableGermlineId: null,
                      selectedConstantGermlineId: null,
                      useMultipleGermlines: false,
                    });
                  }
                  onGermlineSelected?.({
                    germline_id: germlineId,
                    germline_selector_type: 'main',
                    use_multiple_germlines: isMultiple,
                  });
                }}
                disabled={false}
                extraOptions={
                  selectedFormatHasBothDomainTypes
                    ? [{name: 'Multiple germlines', value: MULTIPLE_GERMLINES_SENTINEL}]
                    : []
                }
              />
            </FormGroup>
          </Grid.Col>
        )}
      </Grid>
      {showGermlineSelectors && germlineConfig.useMultipleGermlines && (
        <Grid mt="xs">
          <Grid.Col
            span={6}
            {...getTestProps({component: 'SelectFormatPane', element: 'variableGermlineSelector'})}
          >
            <FormGroup>
              <FieldLabel label="Variable domain germline" isRequired={true} />
              <GermlineSelector
                selectedGermlineId={germlineConfig.selectedVariableGermlineId}
                onSelectGermline={(germlineId: string) => {
                  setGermlineConfig({...germlineConfig, selectedVariableGermlineId: germlineId});
                  onGermlineSelected?.({
                    germline_id: germlineId,
                    germline_selector_type: 'variable_domain',
                    use_multiple_germlines: true,
                  });
                }}
                disabled={false}
              />
            </FormGroup>
          </Grid.Col>
          <Grid.Col
            span={6}
            {...getTestProps({component: 'SelectFormatPane', element: 'constantGermlineSelector'})}
          >
            <FormGroup>
              <FieldLabel label="Constant domain germline" isRequired={true} />
              <GermlineSelector
                selectedGermlineId={germlineConfig.selectedConstantGermlineId}
                onSelectGermline={(germlineId: string) => {
                  setGermlineConfig({...germlineConfig, selectedConstantGermlineId: germlineId});
                  onGermlineSelected?.({
                    germline_id: germlineId,
                    germline_selector_type: 'constant_domain',
                    use_multiple_germlines: true,
                  });
                }}
                disabled={false}
              />
            </FormGroup>
          </Grid.Col>
        </Grid>
      )}

      <Divider my="xl" />

      <div className={classes.startingTypeSection}>
        <Title order={5} fw={600}>
          Starting protein component
        </Title>
        <Text size="sm" mb="lg">
          Select the starting component to start creating proteins
        </Text>
        <Chip.Group
          // We map VARIABLE_PAIRS to DOMAINS since this selector must be set to DOMAINS for the Variable Pairs section
          // below to be shown. The selector below handles setting the starting type to VARIABLE_PAIRS.
          value={
            startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS
              ? NdcSpreadsheetComponentFormatType.DOMAINS
              : startingType
          }
          onChange={(value) => {
            setStartingType(value as NdcSpreadsheetComponentFormatType);
          }}
          multiple={false}
        >
          <Grid gutter="md">
            <Grid.Col span={6}>
              <SelectableCard
                value={NdcSpreadsheetComponentFormatType.DOMAINS}
                title="Domains"
                description="Provide domain sequences that are combined to create chains"
                icon={
                  <Box w="60px" h="60px">
                    <Image
                      src="/static/img/antibody-format-domains.svg"
                      alt="Example of domains on an IgG Monospecific Antibody"
                    />
                  </Box>
                }
                dataTestAttrs={{component: 'SelectFormatPane', element: 'domainsStartingTypeCard'}}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <SelectableCard
                value={NdcSpreadsheetComponentFormatType.CHAINS}
                title="Chains"
                description="Provide chain sequences that are broken into component domains"
                icon={
                  <Box w="60px" h="60px">
                    <Image
                      src="/static/img/antibody-format-chains.svg"
                      alt="Example of chains on an IgG Monospecific Antibody"
                    />
                  </Box>
                }
                dataTestAttrs={{component: 'SelectFormatPane', element: 'chainsStartingTypeCard'}}
              />
            </Grid.Col>
          </Grid>
        </Chip.Group>

        {(startingType === NdcSpreadsheetComponentFormatType.DOMAINS ||
          startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) &&
          (selectedFormat == null || selectedFormatHasVariablePairs) && (
            <div
              className={classes.variablePairsSection}
              {...getTestProps({component: 'SelectFormatPane', element: 'variablePairsSection'})}
            >
              <Radio.Group
                name="startingTypeVariablePairs"
                label={
                  <Group spacing="xs" align="center" noWrap={true}>
                    <Text span={true} fw={FONT_WEIGHT_PROXIMA_SEMIBOLD} color="black">
                      Are you using variable pairs as inputs?
                    </Text>
                    <Tooltip
                      label={<VariablePairsTooltipLabel />}
                      styles={{
                        tooltip: {
                          backgroundColor: 'white',
                          borderRadius: '3px',
                          padding: 0,
                        },
                      }}
                      position="right"
                    >
                      <ActionIcon
                        aria-label="What is a variable pair?"
                        variant="filled"
                        sx={{backgroundColor: Color.GRAY_400, '&:hover': {backgroundColor: Color.GRAY_500}}}
                        radius="xl"
                        size="9px"
                      >
                        <BenchlingIcon icon="question" />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                }
                value={startingType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS ? 'yes' : 'no'}
                onChange={(value) => {
                  setStartingType(
                    value === 'yes'
                      ? NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS
                      : NdcSpreadsheetComponentFormatType.DOMAINS
                  );
                }}
                styles={{
                  label: {
                    marginBottom: '4px',
                  },
                }}
              >
                <Group mt="sm">
                  <Radio value="yes" label="Yes" />
                  <Radio value="no" label="No" />
                </Group>
              </Radio.Group>
            </div>
          )}
      </div>
    </div>
  );
}
